"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { Star, TrendingDown, TrendingUp } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { useApi } from "@/core/api";
import { formatCompactNumber, formatNumber } from "@/core/numbers";
import { useSessionStatus } from "@/core/session";
import { cx } from "@/ds";
import { toast } from "@/ds/kit/toast";

/* ---------- Usage chip ---------- */

/** Tokens served for this model in the last week, linking to rankings. */
export function UsageChip({
  modelId
}: {
  modelId: string;
}) {
  const api = useApi();
  const stats = api.useQuery("get", "/public/models/stats", {
    params: {
      query: {
        window: "7d"
      }
    }
  });
  const row = stats.data?.models.find(item => item.modelId === modelId);
  if (!row || row.totalTokens === 0) {
    return null;
  }
  const trend = row.trendPercent;
  return <Link href="/rankings" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs text-muted-foreground hover:text-foreground">
			{formatCompactNumber(row.totalTokens)} tokens this week
			{trend !== null ? <span className={cx("inline-flex items-center gap-0.5", trend >= 0 ? "text-emerald-600" : "text-rose-600")}>
					{trend >= 0 ? <TrendingUp className="size-3" /> : <TrendingDown className="size-3" />}
					{Math.abs(Math.round(trend))}%
				</span> : null}
		</Link>;
}

/* ---------- Rating ---------- */

function Stars(t0) {
  const $ = _c(6);
  const {
    value,
    onPick,
    size: t1
  } = t0;
  const size = t1 === undefined ? "size-5" : t1;
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setHover(0);
    t3 = [1, 2, 3, 4, 5];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] !== onPick || $[3] !== shown || $[4] !== size) {
    t4 = <div className="flex" onMouseLeave={t2}>{t3.map(star => <button key={star} type="button" disabled={!onPick} aria-label={`${star} star${star === 1 ? "" : "s"}`} onMouseEnter={() => onPick && setHover(star)} onClick={() => onPick?.(star)} className={onPick ? "cursor-pointer" : "cursor-default"}><Star className={cx(size, star <= Math.round(shown) ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30")} /></button>)}</div>;
    $[2] = onPick;
    $[3] = shown;
    $[4] = size;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}

/**
 * Average rating and recent reviews. Signed-in users who have sent enough
 * requests to the model can leave their own.
 */
export function ModelRatingPanel({
  modelId
}: {
  modelId: string;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    isAuthenticated
  } = useSessionStatus();
  const [comment, setComment] = useState("");
  const summary = api.useQuery("get", "/public/model-ratings", {
    params: {
      query: {
        modelId
      }
    }
  });
  const mine = api.useQuery("get", "/model-ratings", {
    params: {
      query: {
        modelId
      }
    }
  }, {
    enabled: isAuthenticated
  });
  const save = api.useMutation("post", "/model-ratings", {
    onSuccess: () => {
      toast({
        title: "Thanks, your rating is saved."
      });
      void queryClient.invalidateQueries();
    },
    onError: () => toast({
      title: "Your rating wasn't saved.",
      variant: "destructive"
    })
  });
  const average = summary.data?.averageRating ?? null;
  const count = summary.data?.ratingCount ?? 0;
  const eligibility = mine.data?.eligibility;
  const own = mine.data?.rating;
  return <section className="rounded-2xl border border-border p-6">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h2 className="text-lg font-semibold">What users think</h2>
					<div className="mt-2 flex items-center gap-3">
						<Stars value={average ?? 0} />
						<span className="text-sm text-muted-foreground">
							{average !== null ? `${average.toFixed(1)} from ${formatNumber(count)} rating${count === 1 ? "" : "s"}` : "No ratings yet"}
						</span>
					</div>
				</div>
				<div className="text-sm">
					{!isAuthenticated ? <Link href="/login" className="font-medium text-brand hover:underline">
							Sign in to rate this model
						</Link> : eligibility && !eligibility.canRate ? <p className="text-muted-foreground">
							Rate after {eligibility.minimumRequests} requests (you have{" "}
							{eligibility.requestCount}).
						</p> : <div className="flex flex-col items-end gap-2">
							<span className="text-muted-foreground">
								{own ? "Your rating" : "Rate it"}
							</span>
							<Stars value={own?.rating ?? 0} onPick={rating => save.mutate({
            body: {
              modelId,
              rating,
              ...(comment.trim() ? {
                comment: comment.trim()
              } : {})
            }
          })} />
						</div>}
				</div>
			</div>
			{isAuthenticated && eligibility?.canRate ? <textarea value={comment} onChange={event => setComment(event.target.value)} maxLength={2000} rows={2} placeholder="Optional: a sentence on how it worked for you, sent with your stars" className="mt-4 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" /> : null}
			{summary.data?.reviews.length ? <ul className="mt-5 space-y-3">
					{summary.data.reviews.slice(0, 3).map(review => <li key={`${review.authorName}-${review.createdAt}`} className="rounded-lg bg-muted/40 p-3 text-sm">
							<div className="flex items-center justify-between gap-2">
								<span className="font-medium">{review.authorName}</span>
								<Stars value={review.rating} size="size-3.5" />
							</div>
							<p className="mt-1 text-muted-foreground">{review.comment}</p>
						</li>)}
				</ul> : null}
		</section>;
}

/* ---------- Performance ---------- */

function cell(value: number | null, format: (value: number) => string) {
  return value === null ? "n/a" : format(value);
}

/** Measured speed and reliability per provider, plus public leaderboard ranks. */
export function ModelPerformance({
  modelId
}: {
  modelId: string;
}) {
  const api = useApi();
  const query = api.useQuery("get", "/internal/models/{modelId}/benchmarks", {
    params: {
      path: {
        modelId
      }
    }
  });
  const rows = (query.data?.providers ?? []).filter(row => row.logsCount > 0);
  const arena = query.data?.arena;
  if (!rows.length && !arena?.text && !arena?.code) {
    return null;
  }
  return <section className="rounded-2xl border border-border p-6">
			<h2 className="text-lg font-semibold">Measured performance</h2>
			{rows.length ? <>
					<p className="mt-1 text-sm text-muted-foreground">
						From real traffic over the last {rows[0]?.windowHours ?? 24} hours.
					</p>
					<div className="mt-4 overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="text-left text-xs text-muted-foreground">
									<th className="py-2 pr-4 font-medium">Provider</th>
									<th className="py-2 pr-4 font-medium">Uptime</th>
									<th className="py-2 pr-4 font-medium">First token</th>
									<th className="py-2 pr-4 font-medium">Throughput</th>
									<th className="py-2 font-medium">Requests</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-border tabular-nums">
								{rows.map(row_0 => <tr key={row_0.providerId}>
										<td className="py-2 pr-4">{row_0.providerName}</td>
										<td className="py-2 pr-4">
											{cell(row_0.uptime, value => `${value.toFixed(1)}%`)}
										</td>
										<td className="py-2 pr-4">
											{cell(row_0.avgTimeToFirstToken, value_0 => `${Math.round(value_0)} ms`)}
										</td>
										<td className="py-2 pr-4">
											{cell(row_0.tokensPerSecond, value_1 => `${Math.round(value_1)} tok/s`)}
										</td>
										<td className="py-2">{formatNumber(row_0.logsCount)}</td>
									</tr>)}
							</tbody>
						</table>
					</div>
				</> : null}
			{arena?.text || arena?.code ? <div className="mt-5 flex flex-wrap gap-3">
					{(["text", "code"] as const).map(kind => {
        const entry = arena[kind];
        return entry ? <div key={kind} className="rounded-lg bg-muted/40 px-4 py-2 text-sm">
								<span className="text-muted-foreground">
									{kind === "text" ? "Text" : "Coding"} leaderboard
								</span>{" "}
								<strong>#{entry.rank}</strong>
								<span className="text-muted-foreground">
									{" "}
									· score {Math.round(entry.score)}
								</span>
							</div> : null;
      })}
					<span className="self-center text-xs text-muted-foreground">
						Source: {arena.source}
					</span>
				</div> : null}
		</section>;
}